import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  alpha,
} from '@mui/material';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import StoreIcon from '@mui/icons-material/Store';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InventoryIcon from '@mui/icons-material/Inventory2';
import axiosInstance from '../lib/axios';
import type { Inventory, Product } from '../types';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette, typography, theme } from '../theme/colors';

interface InventoryWithLocation extends Inventory {
  locationName?: string;
}

interface ProductInventoryDialogProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
}

const fetchProductInventory = async (productId: string): Promise<InventoryWithLocation[]> => {
  // Use the authenticated endpoint that returns all inventory entries for a product
  const response = await axiosInstance.get(`/inventory/product/${productId}/all`);
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

export const ProductInventoryDialog = ({
  open,
  onClose,
  product,
}: ProductInventoryDialogProps) => {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);

  const { data: inventory = [], isLoading, error } = useQuery({
    queryKey: ['productInventory', product?.id],
    queryFn: () => fetchProductInventory(product!.id),
    enabled: open && !!product?.id,
  });

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleAdjustStock = (item: InventoryWithLocation) => {
    onClose();
    navigate(`/app/inventory?productId=${product?.id}`);
  };

  const handleTransfer = (item: InventoryWithLocation) => {
    onClose();
    navigate(`/app/stock-transfers/create?fromLocationId=${item.locationId}&productId=${product?.id}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          border: `2px solid ${palette.primary}`,
          borderRadius: theme.radius.md,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: typography.fontFamily.display,
          fontWeight: typography.fontWeight.bold,
          color: colors.textPrimary,
          textTransform: 'uppercase',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <InventoryIcon sx={{ color: palette.primary }} />
        Inventory
        {product && (
          <Typography
            component="span"
            sx={{
              fontWeight: typography.fontWeight.medium,
              color: colors.textMuted,
              textTransform: 'none',
              ml: 1,
            }}
          >
            — {product.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {isLoading && (
          <Box sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: theme.radius.sm }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: theme.radius.sm }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load inventory data. Please try again.
          </Alert>
        )}

        {!isLoading && !error && (
          <>
            {inventory.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <InventoryIcon sx={{ fontSize: 60, color: colors.textMuted, mb: 2 }} />
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.bold,
                    mb: 1,
                  }}
                >
                  NO INVENTORY FOUND
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  This product is not stocked at any location yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${colors.border}`,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: colors.surfaceElevated,
                        borderBottom: `2px solid ${palette.primary}`,
                      }}
                    >
                      <TableCell
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                          py: 2,
                        }}
                      >
                        Location
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Reserved
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Allocated
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Available
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventory.map((item) => {
                      const available = (item.quantity || 0) - (item.reserved || 0) - (item.allocated || 0);
                      const threshold = item.lowStockThreshold || 10;
                      const isOutOfStock = available <= 0;
                      const isLowStock = available > 0 && available <= threshold;

                      return (
                        <TableRow
                          key={item.id}
                          sx={{
                            transition: theme.transitions.fast,
                            borderLeft: `4px solid ${isOutOfStock ? palette.error : isLowStock ? palette.warning : palette.primary}`,
                            '&:hover': {
                              backgroundColor: alpha(isOutOfStock ? palette.error : isLowStock ? palette.warning : palette.primary, 0.05),
                            },
                          }}
                        >
                          {/* Location */}
                          <TableCell sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.location?.type === 'warehouse' ? (
                                <WarehouseIcon sx={{ fontSize: 18, color: palette.primary }} />
                              ) : (
                                <StoreIcon sx={{ fontSize: 18, color: palette.info }} />
                              )}
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: typography.fontFamily.display,
                                    fontWeight: typography.fontWeight.semibold,
                                    color: colors.textPrimary,
                                    fontSize: typography.fontSize.sm,
                                  }}
                                >
                                  {item.locationName || item.location?.name || item.locationId}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: typography.fontFamily.mono,
                                    color: colors.textMuted,
                                    fontSize: typography.fontSize.xs,
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {item.location?.type === 'warehouse' ? 'Warehouse' : 'Store'}
                                  {item.storageLocation && ` — ${item.storageLocation}`}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Total */}
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                px: 2,
                                py: 0.75,
                                borderRadius: theme.radius.xs,
                                backgroundColor: colors.surfaceElevated,
                                border: `1px solid ${colors.border}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: typography.fontSize.sm,
                                  fontWeight: typography.fontWeight.extrabold,
                                  color: colors.textPrimary,
                                }}
                              >
                                {item.quantity || 0}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Reserved */}
                          <TableCell align="center">
                            {item.reserved > 0 ? (
                              <Chip
                                label={item.reserved}
                                sx={{
                                  backgroundColor: alpha(palette.warning, 0.1),
                                  color: palette.warning,
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: typography.fontSize.xs,
                                  fontWeight: typography.fontWeight.bold,
                                  border: `1px solid ${palette.warning}`,
                                  minWidth: 50,
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: typography.fontSize.sm,
                                  color: colors.textMuted,
                                }}
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>

                          {/* Allocated */}
                          <TableCell align="center">
                            {item.allocated > 0 ? (
                              <Chip
                                label={item.allocated}
                                sx={{
                                  backgroundColor: alpha(palette.info, 0.1),
                                  color: palette.info,
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: typography.fontSize.xs,
                                  fontWeight: typography.fontWeight.bold,
                                  border: `1px solid ${palette.info}`,
                                  minWidth: 50,
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: typography.fontSize.sm,
                                  color: colors.textMuted,
                                }}
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>

                          {/* Available */}
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                px: 2,
                                py: 0.75,
                                borderRadius: theme.radius.xs,
                                backgroundColor: isOutOfStock
                                  ? alpha(palette.error, 0.1)
                                  : isLowStock
                                  ? alpha(palette.warning, 0.1)
                                  : alpha(palette.success, 0.1),
                                border: `2px solid ${
                                  isOutOfStock ? palette.error : isLowStock ? palette.warning : palette.success
                                }`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: typography.fontSize.sm,
                                  fontWeight: typography.fontWeight.extrabold,
                                  color: isOutOfStock ? palette.error : isLowStock ? palette.warning : palette.success,
                                }}
                              >
                                {available}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              {available > 0 && item.location?.canSendTransfers && (
                                <Tooltip title="Transfer Stock">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleTransfer(item)}
                                    sx={{
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: theme.radius.xs,
                                      color: colors.textPrimary,
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        borderColor: palette.info,
                                        backgroundColor: alpha(palette.info, 0.1),
                                        color: palette.info,
                                        transform: 'scale(1.1)',
                                      },
                                      '&:active': {
                                        transform: 'scale(0.95)',
                                      },
                                    }}
                                  >
                                    <SwapHorizIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Adjust Stock">
                                <IconButton
                                  size="small"
                                  onClick={() => handleAdjustStock(item)}
                                  sx={{
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: theme.radius.xs,
                                    color: colors.textPrimary,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: palette.primary,
                                      backgroundColor: alpha(palette.primary, 0.1),
                                      color: palette.primary,
                                      transform: 'scale(1.1)',
                                    },
                                    '&:active': {
                                      transform: 'scale(0.95)',
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceElevated,
          px: 3,
          py: 2,
        }}
      >
        <Button
          startIcon={<AddIcon />}
          onClick={() => {
            onClose();
            navigate(`/app/inventory/create?productId=${product?.id}`);
          }}
          sx={{
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            textTransform: 'uppercase',
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.wide,
            color: palette.primary,
            border: `1px solid ${palette.primary}`,
            borderRadius: theme.radius.xs,
            '&:hover': {
              backgroundColor: alpha(palette.primary, 0.1),
            },
          }}
        >
          Add Location
        </Button>
        <Button
          onClick={onClose}
          sx={{
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            textTransform: 'uppercase',
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.wide,
            color: colors.textMuted,
            '&:hover': {
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
